import React from "react";
import { Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import MapIcon from "assets/images/map-icon.png";
import { useNavigate } from "react-router-dom";
import "./appointmentSlot.scss";
import {
   AppointmentSlotStatusBadge,
   IAppointmentSlotStatusProps,
} from "./AppointmentSlotStatusBadge";

// SCREDP-42: data needed to search for future drives to rebook
export interface IRebookInfo {
   accountName: string;
   locationCode: string;
}

interface IAppointmentSlotProps {
   month: string;
   day: string;
   iconSrc: string;
   name: string;
   // include account name (SCREDP-53)
   accountName?: string | undefined,
   locationName: string,
   subTitle: string;
   rightTopIcon?: JSX.Element;
   rightTopLabel?: string;
   rightTopValue: string;
   rightBottomLabel: string;
   rightBottomValue: string;
   status?: IAppointmentSlotStatusProps;
   mapDirections?: {
      from: string;
      to: string;
   };
   actionButtons?: {
      imageSrc: string;
      onClick: () => void;
      text: string;
   }[];
   rebookInfo?: IRebookInfo | null;
}
// Shared for future and past appointments
export function AppointmentSlot(props: IAppointmentSlotProps) {
   const {
      month,
      day,
      iconSrc,
      name,
      // include account name (SCREDP-53)
      accountName,
      locationName,
      subTitle,
      rightTopLabel,
      rightTopValue,
      rightTopIcon,
      rightBottomLabel,
      rightBottomValue,
      status,
      mapDirections,
      actionButtons,
      rebookInfo,
   } = props;

   // SCREDP-42: Handles the rebooking action by navigating to the homepage with specific query parameters
   const navigate = useNavigate();
   const handleRebook = () => {
      if (rebookInfo) {
         const searchParams = new URLSearchParams();
         if (rebookInfo.accountName) searchParams.set('accountName', rebookInfo.accountName);
         if (rebookInfo.locationCode) searchParams.set('locationCode', rebookInfo.locationCode);
         navigate(`/?${searchParams.toString()}`, { replace: true });
      }
   };


   return (
      <div className={"appointment-slot"}>
         <div className={"date"}>
            <p className={"month"}>{month}</p>
            <p className={"day"}>{day}</p>
         </div>
         <div className={"detail-section"}>
            <div className={"left-section"}>
               <p className={"title"}>
                  <Image src={iconSrc} />
                  <span>{name}</span>
               </p>
               <p className={"sub-title"}>
                  {/* include account name (SCREDP-53) */}
                  {accountName && name !== accountName && <span><b>{accountName}</b> - </span>}
                  {/* Show location name (SRCEDP-63) */}
                  {/* If the account name and the location are the same, only show the account name (SCREDP-63) */}
                  {name !== locationName && locationName && (!accountName || name === accountName ) && (
                     // SCREDP-42: if the location name is longer than 100 chars, truncate it and add a tooltip
                     <OverlayTrigger placement="top" overlay={<Tooltip>{locationName}</Tooltip>}>
                        <span><b>
                           {locationName.length > 100 ? `${locationName.slice(0, 100)}...` : locationName}
                        </b> {" - "} </span>
                     </OverlayTrigger>
                  )}
                  {name !== locationName && locationName && accountName && name !== accountName && (
                     <OverlayTrigger placement="top" overlay={<Tooltip>{locationName}</Tooltip>}>
                        <span>{locationName.length > 100 ? `${locationName.slice(0, 100)}...` : locationName} {" - "} </span>
                     </OverlayTrigger>
                  )}
                  <span>{subTitle}</span>
               </p>
               {rebookInfo && <button onClick={handleRebook}>Schedule here again!</button> /* SCREDP-42: rebook functionality */}
            </div>
            <div className={"right-section"}>
               <div className={"labels"}>
                  <p className="sub-title">
                     {rightTopIcon && rightTopIcon}
                     {rightTopLabel && `${rightTopLabel}: `}
                     <b>{rightTopValue}</b>
                  </p>
                  <p className="sub-title">
                     {rightBottomLabel}: <b>{rightBottomValue}</b>
                  </p>
               </div>
               {mapDirections && (
                  <div
                     className={"map-directions"}
                     onClick={() => {
                        const origin: string = mapDirections.from;
                        const destination: string = mapDirections.to;
                        window.open(
                           `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=car`,
                        );
                     }}
                  >
                     <Image src={MapIcon} />
                  </div>
               )}
            </div>
         </div>

         {status && (
            <AppointmentSlotStatusBadge type={status.type} text={status.text} />
         )}

         {actionButtons && (
            <div className={"actions"}>
               {actionButtons.map((actionButton, index) => (
                    <button onClick={actionButton.onClick} key={index}>
                      <Image src={actionButton.imageSrc} />
                      {actionButton.text}
                    </button>
               ))}
            </div>
         )}
      </div>
   );
}
